import {
  cartItemMatchesManualMutationTarget,
  cartItemMatchesMutationTarget,
  isAutoExtraCartLine,
  type CartLineIdentity,
  type CartLineMutationTarget,
} from "./cartIdentity";

type EventProductUuidLike = {
  productUuid?: unknown;
};

type CartLineWithQuantity = CartLineIdentity & {
  quantity: number;
};

export function resolveEventProductUuid(
  entity: EventProductUuidLike
): string | undefined {
  if (typeof entity.productUuid !== "string") {
    return undefined;
  }
  const trimmed = entity.productUuid.trim();
  return trimmed || undefined;
}


export function eventCartMutationTarget({
  productUuid,
  eventRef,
}: {
  productUuid: string;
  eventRef?: string;
}): CartLineMutationTarget {
  return {
    productUuid,
    eventRef,
  };
}

export function getEventCartLineQuantity(
  cartItems: CartLineWithQuantity[],
  target: CartLineMutationTarget,
): number {
  const line = cartItems.find((item) =>
    cartItemMatchesMutationTarget(item, target)
  );
  return line?.quantity ?? 0;
}

/** Quantity on a user-controlled line; ignores parent-derived auto extras. */
export function getEventManualCartLineQuantity(
  cartItems: CartLineWithQuantity[],
  target: CartLineMutationTarget,
): number {
  const line = cartItems.find((item) =>
    cartItemMatchesManualMutationTarget(item, target)
  );
  return line?.quantity ?? 0;
}

type EventTicketCatalog = {
  ticketProducts?: Array<{ productUuid?: unknown }>;
};

export function collectEventTicketProductUuids(
  event: EventTicketCatalog
): Set<string> {
  const uuids = new Set<string>();
  for (const ticket of event.ticketProducts ?? []) {
    const uuid = resolveEventProductUuid(ticket);
    if (uuid) {
      uuids.add(uuid);
    }
  }
  return uuids;
}

/** Ticket seat usage for an event (excludes auto extras and additional services). */
export function getEventTicketCartQuantity(
  cartItems: CartLineWithQuantity[],
  eventRef: string,
  ticketProductUuids: Set<string>,
): number {
  return cartItems
    .filter((item) => {
      if (item.eventHash !== eventRef) {
        return false;
      }
      if (isAutoExtraCartLine(item)) {
        return false;
      }
      return ticketProductUuids.has(item.productUuid);
    })
    .reduce((sum, item) => sum + item.quantity, 0);
}

export function getEventCartInputKey(
  productUuid: string,
  eventRef: string
): string {
  return `${productUuid}-${eventRef}`;
}
